import {TableColumn} from "../../../Component/Model/Table/Table";
import {DefaultCell} from "../../../Component/Common/Components/DataTable/DefaultCell";
import {Box, Chip} from "@mui/material";
import {ValidationReview} from "../../../Component/Model/Dto/Staging/ValidationReview";

interface ValidationReviewEmployeeRow {
    name: string;
    employeeCode: string;
    idNumber: string;
    failures: ValidationReview.Failure[];
}

interface ValidationReviewEmployeeTable {
    columns: Array<TableColumn>;
    rows: Array<ValidationReviewEmployeeRow>;
}

// The categories are a small closed set (mirrors the backend enum); render them human-readably.
const categoryLabels: Record<ValidationReview.ValidationFailureCategory, string> = {
    [ValidationReview.ValidationFailureCategory.Unspecified]: "Unspecified",
    [ValidationReview.ValidationFailureCategory.Required]: "Required",
    [ValidationReview.ValidationFailureCategory.InvalidValue]: "Invalid value",
    [ValidationReview.ValidationFailureCategory.InvalidDateRange]: "Invalid date range",
    [ValidationReview.ValidationFailureCategory.NotFound]: "Not found",
    [ValidationReview.ValidationFailureCategory.Duplicate]: "Duplicate",
};

export const failureLabel = (failure: ValidationReview.Failure): string => {
    const category = categoryLabels[failure.category] ?? "Unspecified";
    const field = failure.field ? `: ${failure.field}` : "";
    const section = failure.section ? ` (${failure.section})` : "";
    return `${category}${field}${section}`;
};

// The categorised reason without the section (shown as its own column for the unattributed table).
export const failureReason = (failure: ValidationReview.Failure): string => {
    const category = categoryLabels[failure.category] ?? "Unspecified";
    return failure.field ? `${category}: ${failure.field}` : category;
};

const FailuresCell = ({value}: { value: ValidationReview.Failure[] }) => (
    <Box sx={{display: "flex", flexWrap: "wrap", gap: 1}}>
        {value.map((failure, index) => (
            <Chip key={index} label={failureLabel(failure)} size="small" color="error" variant="outlined"/>
        ))}
    </Box>
);

export function PayrollValidationReviewMapper(employees: ValidationReview.Employee[]): ValidationReviewEmployeeTable {
    const columns = [
        {Header: 'Employee', accessor: 'name', Cell: ({value}: any) => <DefaultCell value={value}/>, disableSortBy: true},
        {Header: 'Employee Code', accessor: 'employeeCode', Cell: ({value}: any) => <DefaultCell value={value}/>, disableSortBy: true},
        {Header: 'ID Number', accessor: 'idNumber', Cell: ({value}: any) => <DefaultCell value={value}/>, disableSortBy: true},
        {Header: 'Failures', accessor: 'failures', Cell: FailuresCell, disableSortBy: true},
    ];

    const rows = employees.map(employee => ({
        name: `${employee.firstname ?? ""} ${employee.surname ?? ""}`.trim(),
        employeeCode: employee.employeeCode ?? "",
        idNumber: employee.idNumber ?? "",
        failures: employee.failures,
    }));

    return {columns, rows};
}

interface UnattributedFailureRow {
    section: string;
    reason: string;
}

interface UnattributedFailureTable {
    columns: Array<TableColumn>;
    rows: Array<UnattributedFailureRow>;
}

export function PayrollUnattributedFailuresMapper(failures: ValidationReview.Failure[]): UnattributedFailureTable {
    const columns = [
        {Header: 'Section', accessor: 'section', Cell: ({value}: any) => <DefaultCell value={value}/>, disableSortBy: true},
        {Header: 'Failure', accessor: 'reason', Cell: ({value}: any) => <DefaultCell value={value}/>, disableSortBy: true},
    ];

    const rows = failures.map(failure => ({
        section: failure.section ?? "",
        reason: failureReason(failure),
    }));

    return {columns, rows};
}
